/**
 * Consent-aware website -> WhatsApp support correlation.
 *
 * Every wa.me anchor is decorated at click time so static Liquid links and
 * links synthesized by other theme scripts share one implementation. The
 * opaque reference is correlation only; it never authorizes data access.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'alasil_support_journey_v1';
  var SESSION_TTL_MS = 2 * 60 * 60 * 1000;
  var HEARTBEAT_MS = 25000;
  var ACTIVE_WINDOW_MS = 60000;
  var REFERENCE_RE = /(?:\r?\n|^)\s*(?:Reference:\s*ALASIL-[A-Z2-9]{16}|Request ID:\s*[A-Z2-9]{5}-[A-Z2-9]{5})\s*$/i;
  var INTERNAL_REFERENCE_RE = /^ALASIL-(?:[A-Z2-9]{10}|[A-Z2-9]{16})$/;
  var ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  var session = null;
  var heartbeatTimer = null;
  var lastActivityAt = Date.now();
  var observedCartDrawer = null;
  var cartDrawerObserver = null;
  var lastDrawerOpen = false;
  var lastDrawerStep = 'cart';
  var cartSnapshotTimer = null;
  var lastCartFingerprint = null;

  function analyticsAllowed() {
    try {
      return window.Shopify &&
        Shopify.customerPrivacy &&
        typeof Shopify.customerPrivacy.analyticsProcessingAllowed === 'function' &&
        Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  }

  function loadPrivacyApi() {
    try {
      if (window.Shopify && Shopify.customerPrivacy) {
        resumeSession();
        return;
      }
      if (!window.Shopify || typeof Shopify.loadFeatures !== 'function') return;
      Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) resumeSession();
      });
    } catch (_) {}
  }

  function newReference() {
    var bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
    } else {
      for (var i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    var result = '';
    for (var j = 0; j < 10; j += 1) result += ALPHABET[bytes[j] & 31];
    return 'ALASIL-' + result;
  }

  function publicRequestId(ref) {
    var code = String(ref || '').replace(/^ALASIL-/, '');
    return code.length === 10 ? code.slice(0, 5) + '-' + code.slice(5) : code;
  }

  function saveSession(next) {
    session = next;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
  }

  function clearSession() {
    session = null;
    lastCartFingerprint = null;
    if (heartbeatTimer) window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  function readSession() {
    try {
      var parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!parsed || !INTERNAL_REFERENCE_RE.test(String(parsed.ref || ''))) return null;
      if (!Number(parsed.expiresAt) || Date.now() >= Number(parsed.expiresAt)) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function publish(name, detail) {
    try {
      if (!analyticsAllowed()) return;
      if (typeof window.publishAlasilEvent === 'function') {
        window.publishAlasilEvent(name, detail);
        return;
      }
      var result = window.Shopify && Shopify.analytics && Shopify.analytics.publish
        ? Shopify.analytics.publish(name, detail)
        : null;
      if (result && typeof result.catch === 'function') result.catch(function () {});
    } catch (_) {}
  }

  function pageDetail(extra) {
    var path = String(window.location.pathname || '/');
    var productMatch = path.match(/^\/products\/([^/?#]+)/i);
    return Object.assign({
      support_ref: session && session.ref,
      source: session && session.source,
      page_title: String(document.title || '').slice(0, 160),
      page_type: productMatch ? 'product' : path === '/' ? 'home' : path.split('/').filter(Boolean)[0] || 'page',
      product_handle: productMatch ? productMatch[1].slice(0, 255) : null,
    }, extra || {});
  }

  function safeCartStep(value) {
    value = String(value || 'cart').toLowerCase();
    return /^(cart|identity|address|contact|checkout)$/.test(value) ? value : 'cart';
  }

  function safeCartItems(items) {
    if (!Array.isArray(items)) return [];
    return items.slice(0, 25).map(function (item) {
      var quantity = Math.max(0, Math.min(999, Number(item && item.quantity) || 0));
      var unitCents = item && item.final_price != null ? Number(item.final_price) : Number(item && item.price);
      var lineCents = item && item.final_line_price != null ? Number(item.final_line_price) : Number(item && item.line_price);
      return {
        product_id: item && item.product_id != null ? String(item.product_id).slice(0, 32) : null,
        variant_id: item && item.variant_id != null ? String(item.variant_id).slice(0, 32) : null,
        product_handle: item && item.handle ? String(item.handle).slice(0, 255) : null,
        title: item && (item.product_title || item.title) ? String(item.product_title || item.title).slice(0, 255) : null,
        variant_title: item && item.variant_title ? String(item.variant_title).slice(0, 160) : null,
        quantity: quantity,
        unit_price: Number.isFinite(unitCents) ? Math.round(unitCents) / 100 : null,
        line_total: Number.isFinite(lineCents) ? Math.round(lineCents) / 100 : null,
        image_url: item && (item.featured_image && item.featured_image.url || item.image)
          ? String(item.featured_image && item.featured_image.url || item.image).slice(0, 768)
          : null,
        product_url: item && item.url ? String(item.url).slice(0, 768) : null,
      };
    });
  }

  function publishCartSnapshot(reason, providedCart) {
    if (!session || !analyticsAllowed()) return;
    if (cartSnapshotTimer) window.clearTimeout(cartSnapshotTimer);
    cartSnapshotTimer = window.setTimeout(function () {
      cartSnapshotTimer = null;
      var cartPromise = providedCart && Array.isArray(providedCart.items) && typeof providedCart.item_count === 'number'
        ? Promise.resolve(providedCart)
        : typeof window.getCartSnapshot === 'function'
          ? window.getCartSnapshot({ force: reason === 'mutation' })
          : fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart.js', {
              credentials: 'same-origin', headers: { Accept: 'application/json' }
            }).then(function (response) { return response.ok ? response.json() : null; });
      Promise.resolve(cartPromise).then(function (cart) {
        if (!cart || !session) return;
        var items = safeCartItems(cart.items);
        var fingerprint = JSON.stringify(items.map(function (item) {
          return [item.variant_id, item.quantity, item.line_total];
        }));
        if (fingerprint === lastCartFingerprint && reason === 'mutation') return;
        lastCartFingerprint = fingerprint;
        publish('alasil:support_cart_snapshot', pageDetail({
          reason: String(reason || 'update').slice(0, 24),
          currency: String(cart.currency || window.Shopify && Shopify.currency && Shopify.currency.active || 'AED').slice(0, 8),
          item_count: Math.max(0, Math.min(999, Number(cart.item_count) || 0)),
          subtotal: cart.items_subtotal_price != null ? Number(cart.items_subtotal_price) / 100 : null,
          total: cart.total_price != null ? Number(cart.total_price) / 100 : null,
          items: items,
        }));
      }).catch(function () {});
    }, providedCart || reason === 'handoff' ? 0 : 120);
  }

  // CartDrawer owns the authoritative open/close lifecycle. It calls this
  // directly, while the MutationObserver below remains a replacement-safe
  // fallback for older or third-party drawer implementations.
  function publishDrawerState(state, step) {
    if (!session || !analyticsAllowed()) return;
    var isOpen = state === 'open';
    var nextStep = safeCartStep(step);
    if (isOpen === lastDrawerOpen) return;
    lastDrawerOpen = isOpen;
    lastDrawerStep = nextStep;
    publish(isOpen ? 'alasil:cart_drawer_opened' : 'alasil:cart_drawer_closed', pageDetail({ step: nextStep }));
  }

  function bindCartDrawerTracking() {
    if (typeof document.querySelector !== 'function' || typeof MutationObserver !== 'function') return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer === observedCartDrawer) return;
    if (cartDrawerObserver) cartDrawerObserver.disconnect();
    observedCartDrawer = drawer;
    lastDrawerOpen = drawer.classList.contains('active');
    lastDrawerStep = safeCartStep(drawer.dataset.currentStep);
    cartDrawerObserver = new MutationObserver(function () {
      var isOpen = drawer.classList.contains('active');
      var step = safeCartStep(drawer.dataset.currentStep);
      if (isOpen !== lastDrawerOpen) {
        publishDrawerState(isOpen ? 'open' : 'closed', step);
        if (isOpen) publishCartSnapshot('drawer_opened');
      }
      if (step !== lastDrawerStep) {
        var previousStep = lastDrawerStep;
        lastDrawerStep = step;
        if (isOpen) publish('alasil:cart_drawer_step_changed', pageDetail({
          step: step,
          previous_step: previousStep,
        }));
      }
    });
    cartDrawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class', 'data-current-step'] });
  }

  function publishPresence(state) {
    if (!session || !analyticsAllowed()) return;
    publish('alasil:support_presence', pageDetail({
      state: state || (document.visibilityState === 'visible' ? 'heartbeat' : 'hidden'),
      visible: document.visibilityState === 'visible',
      focused: typeof document.hasFocus === 'function' ? document.hasFocus() : true,
      recent_active: Date.now() - lastActivityAt <= ACTIVE_WINDOW_MS,
    }));
  }

  function startHeartbeat() {
    if (heartbeatTimer || !session) return;
    heartbeatTimer = window.setInterval(function () {
      if (document.visibilityState === 'visible') publishPresence('heartbeat');
    }, HEARTBEAT_MS);
  }

  function resumeSession() {
    if (!analyticsAllowed()) {
      clearSession();
      return;
    }
    session = readSession();
    if (!session) return;
    publishPresence('page_view');
    startHeartbeat();
  }

  function beginHandoff(source) {
    var next = {
      ref: newReference(),
      source: String(source || 'website').slice(0, 64),
      createdAt: Date.now(),
      expiresAt: Date.now() + SESSION_TTL_MS,
    };
    // A short Request ID is operational support context, not browsing analytics.
    // Without analytics consent it is returned for the WhatsApp message only:
    // no local storage, pixel event, presence, or cart data is collected.
    if (!analyticsAllowed()) return next.source === 'availability_request' ? next.ref : null;
    lastCartFingerprint = null;
    saveSession(next);
    publish('alasil:support_handoff_started', pageDetail());
    publishPresence('handoff');
    bindCartDrawerTracking();
    if (observedCartDrawer && observedCartDrawer.classList.contains('active')) {
      publish('alasil:cart_drawer_opened', pageDetail({ step: safeCartStep(observedCartDrawer.dataset.currentStep) }));
      publishCartSnapshot('handoff');
    }
    startHeartbeat();
    return next.ref;
  }

  function defaultMessage() {
    var title = String(document.title || '').replace(/\s*[|\-–]\s*alAsil.*$/i, '').trim();
    return title && title.toLowerCase() !== 'alasil'
      ? "Hi! I'd like some assistance with " + title + '.'
      : "Hi! I'd like some assistance.";
  }

  function isWhatsAppUrl(value) {
    try {
      var url = new URL(String(value || ''), window.location.href);
      return /^(?:www\.)?wa\.me$/i.test(url.hostname) || /^api\.whatsapp\.com$/i.test(url.hostname);
    } catch (_) {
      return false;
    }
  }

  function decorateWhatsAppUrl(value, source) {
    try {
      var url = new URL(String(value || ''), window.location.href);
      if (!isWhatsAppUrl(url.href)) return value;
      var message = String(url.searchParams.get('text') || defaultMessage()).replace(REFERENCE_RE, '').trim();
      var ref = beginHandoff(source);
      if (ref) message += '\n\nRequest ID: ' + publicRequestId(ref);
      url.searchParams.set('text', message);
      return url.toString();
    } catch (_) {
      return value;
    }
  }

  function sourceFor(anchor) {
    if (anchor.dataset && anchor.dataset.supportSource) return anchor.dataset.supportSource;
    if (anchor.closest('#wa-panel, #wa-widget')) return 'whatsapp_widget';
    if (anchor.closest('.sticky-cart, #sticky-cart')) return 'sticky_cart';
    if (anchor.closest('.pcard, .card-wrapper')) return 'product_card';
    if (anchor.closest('.product, .product__info-container')) return 'product_page';
    if (anchor.closest('footer, .footer, .fv2')) return 'footer';
    if (anchor.closest('.mobile-menu, .menu-drawer, .mm-drawer')) return 'mobile_menu';
    if (anchor.closest('.trust-modal, .trust-page')) return 'trust';
    if (anchor.closest('.search, .sr__whatsapp')) return 'search';
    return 'website_link';
  }

  document.addEventListener('click', function (event) {
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor || !isWhatsAppUrl(anchor.href)) return;
    anchor.href = decorateWhatsAppUrl(anchor.href, sourceFor(anchor));
  }, true);

  function markActivity() { lastActivityAt = Date.now(); }
  document.addEventListener('pointerdown', markActivity, { passive: true });
  document.addEventListener('keydown', markActivity, true);
  document.addEventListener('scroll', markActivity, { passive: true });
  document.addEventListener('visibilitychange', function () {
    if (!session) return;
    publishPresence(document.visibilityState === 'visible' ? 'visible' : 'hidden');
  });
  document.addEventListener('cart:updated', function (event) {
    publishCartSnapshot('mutation', event && event.detail && event.detail.cart);
  });
  window.addEventListener('pagehide', function () { if (session) publishPresence('hidden'); });
  document.addEventListener('visitorConsentCollected', function (event) {
    if (event && event.detail && event.detail.analyticsAllowed === false) clearSession();
    else resumeSession();
  });

  bindCartDrawerTracking();
  window.AlasilSupportJourney = {
    decorateWhatsAppUrl: decorateWhatsAppUrl,
    publishDrawerState: publishDrawerState,
    publishCartSnapshot: publishCartSnapshot,
  };
  loadPrivacyApi();
})();
